import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ABO_PROFILE_TABS_ID, CUSTOMER_PROFILE_TABS_ID } from "../common/enums/profile-enums";
import { setBoot } from "./boot";
import type { ProfileTabIds } from "./downlineProfile";

export type ProfileType = "abo" | "customer" | null;

type EdlosAboMeta = {
  level?: number;
  enableDownlineLOS?: boolean;
};
type Profile = {
  affAbo: string;
  isShowProfile: boolean;
  profileType: ProfileType;
  isShowApplicant: boolean;
  customerDefaultTab: string;
  customerActiveTab: string;
  customerActiveTabs: CUSTOMER_PROFILE_TABS_ID[];
  aboDefaultTab: string;
  aboActiveTab: string;
  aboActiveTabs: ABO_PROFILE_TABS_ID[];
  pendingCustomerOrderAboNo: string | null;
  previousElement?: HTMLElement;
  useCurrentMonthData?: boolean;
  showOnlyPerformanceHistory?: boolean;
  confidentialView?: boolean;
  isConfidentialCustomer?: boolean;
  requestWithSponsor?: boolean;
  isShowDownlineProfile: boolean;
  downlineProfileAffAbo: string;
  downlineProfileInitialTab?: ProfileTabIds;
  isFromEdLos: boolean;
  edlosAboMeta: EdlosAboMeta | null;
  isFromActionReport: boolean;
  edlosRootAffAbo: string | null;
  edlosRootDepth: number | null;
};

const initialState: Profile = {
  affAbo: "",
  isShowProfile: false,
  profileType: null,
  isShowApplicant: false,
  customerDefaultTab: "",
  customerActiveTab: "",
  customerActiveTabs: [],
  aboDefaultTab: "",
  aboActiveTab: "",
  aboActiveTabs: [],
  pendingCustomerOrderAboNo: null,
  previousElement: undefined,
  useCurrentMonthData: false,
  showOnlyPerformanceHistory: false,
  confidentialView: false,
  isConfidentialCustomer: false,
  requestWithSponsor: false,
  isShowDownlineProfile: false,
  downlineProfileAffAbo: "",
  downlineProfileInitialTab: undefined,
  isFromEdLos: false,
  edlosAboMeta: null,
  isFromActionReport: false,
  edlosRootAffAbo: null,
  edlosRootDepth: null,
};

const getDefaultTab = (tabs: string[]): string => (Array.isArray(tabs) && tabs.length ? tabs[0] : "");

const { actions, reducer } = createSlice({
  name: "profile",
  initialState,
  reducers: {
    showProfile: (
      state,
      action: PayloadAction<
        | string
        | {
            affAbo: string;
            previousElement?: HTMLElement;
            useCurrentMonthData?: boolean;
            showOnlyPerformanceHistory?: boolean;
            isFromEdLos?: boolean;
            edlosAboMeta?: { level?: number; enableDownlineLOS?: boolean };
            isFromActionReport?: boolean;
            edlosRootAffAbo?: string;
            edlosRootDepth?: number;
            downlineProfileInitialTab?: ProfileTabIds;
          }
      >,
    ) => {
      const affAbo = typeof action.payload === "string" ? action.payload : action.payload.affAbo;
      const previousElement = typeof action.payload === "string" ? undefined : action.payload.previousElement;
      const useCurrentMonthData =
        typeof action.payload === "string" ? false : (action.payload.useCurrentMonthData ?? false);
      const showOnlyPerformanceHistory =
        typeof action.payload === "string" ? false : (action.payload.showOnlyPerformanceHistory ?? false);
      const isFromEdLos = typeof action.payload === "string" ? false : (action.payload.isFromEdLos ?? false);
      const edlosAboMeta = typeof action.payload === "string" ? null : (action.payload.edlosAboMeta ?? null);
      const isFromActionReport =
        typeof action.payload === "string" ? false : (action.payload.isFromActionReport ?? false);
      const edlosRootAffAbo = typeof action.payload === "string" ? null : (action.payload.edlosRootAffAbo ?? null);
      const edlosRootDepth = typeof action.payload === "string" ? null : (action.payload.edlosRootDepth ?? null);
      const downlineProfileInitialTab =
        typeof action.payload === "string" ? undefined : action.payload.downlineProfileInitialTab;
      return {
        ...state,
        affAbo,
        isShowProfile: true,
        profileType: "abo" as const,
        customerActiveTab: state.customerDefaultTab,
        aboActiveTab: state.aboDefaultTab,
        previousElement,
        useCurrentMonthData,
        showOnlyPerformanceHistory,
        isFromEdLos,
        edlosAboMeta,
        isFromActionReport,
        edlosRootAffAbo: isFromEdLos ? edlosRootAffAbo : null,
        edlosRootDepth: isFromEdLos ? edlosRootDepth : null,
        downlineProfileInitialTab,
      };
    },
    showCustomerProfile: (
      state,
      action: PayloadAction<
        | string
        | { affAbo: string; confidentialView?: boolean; isConfidentialCustomer?: boolean; requestWithSponsor?: boolean }
      >,
    ) => {
      const affAbo = typeof action.payload === "string" ? action.payload : action.payload.affAbo;
      const confidentialView = typeof action.payload === "string" ? false : (action.payload.confidentialView ?? false);
      const isConfidentialCustomer =
        typeof action.payload === "string" ? false : (action.payload.isConfidentialCustomer ?? false);
      const requestWithSponsor =
        typeof action.payload === "string" ? false : (action.payload.requestWithSponsor ?? false);
      return {
        ...state,
        affAbo,
        isShowProfile: true,
        profileType: "customer" as const,
        customerActiveTab: state.customerDefaultTab,
        aboActiveTab: state.aboDefaultTab,
        confidentialView,
        isConfidentialCustomer,
        requestWithSponsor,
      };
    },
    hideProfile: (state) => ({
      ...state,
      isShowProfile: false,
      profileType: null,
      customerActiveTab: state.customerDefaultTab,
      aboActiveTab: state.aboDefaultTab,
      previousElement: undefined,
      useCurrentMonthData: false,
      showOnlyPerformanceHistory: false,
      confidentialView: false,
      isConfidentialCustomer: false,
      requestWithSponsor: false,
      isFromEdLos: false,
      edlosAboMeta: null,
      isFromActionReport: false,
      edlosRootAffAbo: null,
      edlosRootDepth: null,
      downlineProfileInitialTab: undefined,
    }),
    showDownlineProfile: (state, action: PayloadAction<string>) => ({
      ...state,
      isShowDownlineProfile: true,
      downlineProfileAffAbo: action.payload,
    }),
    hideDownlineProfile: (state) => ({
      ...state,
      isShowDownlineProfile: false,
      downlineProfileAffAbo: "",
      downlineProfileInitialTab: undefined,
    }),
    showApplicant: (state) => ({
      ...state,
      isShowApplicant: true,
    }),
    hideApplicant: (state) => ({
      ...state,
      isShowApplicant: false,
    }),
    setCustomerActiveTab: (state, action: PayloadAction<string>) => ({
      ...state,
      customerActiveTab: action.payload,
    }),
    setAboActiveTab: (state, action: PayloadAction<string>) => ({
      ...state,
      aboActiveTab: action.payload,
    }),
    setPendingCustomerOrderAboNo: (state, action: PayloadAction<string>) => ({
      ...state,
      pendingCustomerOrderAboNo: action.payload,
    }),
    clearPendingCustomerOrderAboNo: (state) => ({
      ...state,
      pendingCustomerOrderAboNo: null,
    }),
    setEdlosAboMeta: (state, action: PayloadAction<EdlosAboMeta | null>) => {
      state.edlosAboMeta = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setBoot, (state, { payload }) => {
      const { customerProfileTabs, aboProfileTabs } = payload.configuration.profile;
      return {
        ...state,
        customerDefaultTab: getDefaultTab(customerProfileTabs),
        customerActiveTabs: customerProfileTabs,
        aboDefaultTab: getDefaultTab(aboProfileTabs),
        aboActiveTabs: aboProfileTabs,
      };
    });
  },
});

export const {
  showProfile,
  showCustomerProfile,
  hideProfile,
  showDownlineProfile,
  hideDownlineProfile,
  showApplicant,
  hideApplicant,
  setCustomerActiveTab,
  setAboActiveTab,
  setPendingCustomerOrderAboNo,
  clearPendingCustomerOrderAboNo,
  setEdlosAboMeta,
} = actions;

export default reducer;
